import HelixAvatar from '@components/helixAvatar';
import HelixButton from '@components/helixButton';
import HelixInput from '@components/helixInput';
import HelixStack from '@components/helixStack';
import HelixIcon, { HelixIconType } from '@components/icon/icon';
import Mail from '@generated/icons/Mail';
import { ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import { Box, Button, Grid, Paper, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import { Dispatch, FC, SetStateAction, useState } from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import validator from 'validator';

type ForgotPasswordInputs = {
  email: string;
};

type ForgotPasswordProps = {
  backHref: string;
  isForgotPasswordRequestSubmitted: boolean;
  onForgotPasswordSubmit: (data: ForgotPasswordInputs) => void;
};

const ForgotPassword: FC<ForgotPasswordProps> = (props) => {
  const { backHref, isForgotPasswordRequestSubmitted } = props;
  const [userEmail, setUserEmail] = useState('');

  return (
    <Grid container justifyContent="center" spacing={3} columns={{ md: 12, sm: 8, xs: 4 }}>
      <Grid item sm={6} xs={4}>
        <HelixStack spacing={2} justifyContent="left">
          <Box>
            <Button
              variant="text"
              sx={{ paddingLeft: '0' }}
              href={backHref}
              startIcon={
                <HelixIcon type={HelixIconType.MUI}>
                  <ArrowBackIcon />
                </HelixIcon>
              }
            >
              <b>{isForgotPasswordRequestSubmitted ? 'Back to sign in' : 'Back'}</b>
            </Button>
          </Box>
          <Paper elevation={0} sx={{ pb: 4, pt: 4, px: 3 }}>
            {isForgotPasswordRequestSubmitted ? (
              <ForgotPasswordRequestSubmitted backHref={backHref} userEmail={userEmail} />
            ) : (
              <ForgotPasswordBaseView {...props} setUserEmail={setUserEmail} />
            )}
          </Paper>
        </HelixStack>
      </Grid>
    </Grid>
  );
};

type ForgotPasswordBaseViewProps = ForgotPasswordProps & {
  setUserEmail: Dispatch<SetStateAction<string>>;
};
const ForgotPasswordBaseView: FC<ForgotPasswordBaseViewProps> = (props) => {
  const { onForgotPasswordSubmit, setUserEmail } = props;

  const forgotPasswordForm = useForm<ForgotPasswordInputs>({
    mode: 'onChange',
    defaultValues: {
      email: '',
    },
  });

  const {
    control,
    handleSubmit,
    formState: { isSubmitting, isValid },
  } = forgotPasswordForm;

  return (
    <FormProvider {...forgotPasswordForm}>
      <form>
        <HelixStack spacing={4}>
          <HelixStack spacing={1}>
            <Typography variant="h4">Reset password</Typography>
            <Typography variant="body1">
              Provide your email address below and we will send instructions to reset your password.
            </Typography>
          </HelixStack>
          <Controller
            name="email"
            control={control}
            rules={{
              required: 'Please enter an email address',
              validate: (value) => {
                const trimmedValue = value.trim();
                if (!validator.isEmail(trimmedValue)) {
                  return 'Please enter a valid email address';
                }
                return !!trimmedValue;
              },
            }}
            render={({ field, fieldState }) => {
              const { value, onChange, ref } = field;
              const { invalid, error } = fieldState;
              const { message = '' } = error ?? {};
              return (
                <HelixInput
                  id="email"
                  variant="outlined"
                  placeholder="Email address"
                  value={value}
                  onChange={onChange}
                  ref={ref}
                  label="Email address"
                  hasError={invalid}
                  helperText={message}
                />
              );
            }}
          />
          <HelixButton
            sx={{ alignSelf: 'center' }}
            type="submit"
            onClick={handleSubmit(async (data) => {
              setUserEmail(data.email);
              onForgotPasswordSubmit(data);
            })}
            disabled={isSubmitting || !isValid}
            displayText="Submit"
          />
        </HelixStack>
      </form>
    </FormProvider>
  );
};

type ForgotPasswordRequestSubmittedProps = Pick<ForgotPasswordProps, 'backHref'> & {
  userEmail: string;
};
const ForgotPasswordRequestSubmitted: FC<ForgotPasswordRequestSubmittedProps> = (props) => {
  const { backHref, userEmail } = props;

  return (
    <HelixStack spacing={4}>
      <MailAvatar>
        <HelixIcon type={HelixIconType.Helix} rem={3.5} sx={{ alignSelf: 'center' }}>
          <Mail />
        </HelixIcon>
      </MailAvatar>
      <HelixStack spacing={1}>
        <Typography variant="h4">Check your email</Typography>
        <Typography variant="body1">An email has been sent to {userEmail} with further instructions.</Typography>
      </HelixStack>
      <HelixButton sx={{ alignSelf: 'center' }} href={backHref} displayText="Back to sign in" />
    </HelixStack>
  );
};

const MailAvatar = styled(HelixAvatar)(({ theme }) => ({
  backgroundColor: theme.palette.primary.mid,
  color: theme.palette.black.main,
  padding: '2rem',
  height: '8rem',
  width: '8rem',
  alignSelf: 'center',
}));

export { ForgotPassword as default, ForgotPassword };
export type { ForgotPasswordInputs };
export type { ForgotPasswordProps };
